/**
 * IMAGE OPTIMIZER CORE (SSOT)
 * ---------------------------
 * Single Source of Truth for image optimization logic.
 * Handles URL parameter construction for various image providers.
 */

interface OptimizationConfig {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'auto' | 'webp' | 'avif' | 'jpeg';
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
}

interface ImageOptimizationConfigAdvanced extends OptimizationConfig {
  dimensions?: { width: number; height: number };
  priority?: boolean;
  srcset?: boolean;
  sizes?: string;
}

interface ImageDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

interface OptimizedImageResult {
  src: string;
  srcset?: string;
  sizes?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  fetchpriority?: 'high' | 'low' | 'auto';
}

/**
 * Optimizes an image URL by appending provider-specific query parameters.
 * Currently supports generic query-param based services (Unsplash, Supabase, etc.)
 */
export function optimizeImage(
  url: string | null | undefined,
  config: OptimizationConfig = {}
): string {
  if (!url) return '';
  if (url.startsWith('data:')) return url; // Don't touch data URIs
  if (url.startsWith('blob:')) return url; // Don't touch blobs
  if (url.startsWith('/')) return url; // Don't touch local paths

  try {
    const {
      width,
      height,
      quality = 80,
      format = 'auto',
      fit = 'cover'
    } = config;

    const [baseUrl, query] = url.split('?');
    if (!baseUrl) return url;

    // Preserve existing params but override with optimization params
    const params = new URLSearchParams(query);

    // 1. Width & Height
    if (width) params.set('w', width.toString());
    if (height) params.set('h', height.toString());

    // 2. Quality & Format
    params.set('q', quality.toString());
    if (format !== 'auto') params.set('fm', format);
    
    // 3. Fit mode
    // Construct 'fit' param based on provider patterns
    // Unsplash/Imgix style:
    const fitMapping: Record<string, string> = {
      cover: 'crop',
      contain: 'fill',
      fill: 'scale',
      inside: 'max',
      outside: 'min'
    };
    params.set('fit', fitMapping[fit] || 'crop');

    // 4. Density (DPR)
    // Default to 1.5x for crispness on high-res displays without huge file size
    if (!params.has('dpr')) {
      params.set('dpr', '1.5');
    }

    // 5. Auto optimizations (compression, format)
    if (!params.has('auto')) {
      params.set('auto', 'compress,format');
    }

    return `${baseUrl}?${params.toString()}`;
  } catch (error) {
    console.warn('Image optimization failed:', error);
    return url || '';
  }
}

/**
 * Get image dimensions from URL or metadata
 * For external images, this is a best-effort approach
 * Returns default dimensions if detection fails
 */
export async function getImageDimensions(url: string): Promise<ImageDimensions> {
  // Default fallback dimensions
  const defaultDimensions: ImageDimensions = {
    width: 800,
    height: 600,
    aspectRatio: 4 / 3,
  };

  if (!url || url.startsWith('data:') || url.startsWith('blob:')) {
    return defaultDimensions;
  }

  try {
    // For browser environment, use Image API
    if (typeof window !== 'undefined') {
      return new Promise((resolve) => {
        const img = new Image();
        
        // Set timeout to prevent hanging
        const timeout = setTimeout(() => {
          resolve(defaultDimensions);
        }, 3000);

        img.onload = () => {
          clearTimeout(timeout);
          resolve({
            width: img.naturalWidth,
            height: img.naturalHeight,
            aspectRatio: img.naturalWidth / img.naturalHeight,
          });
        };

        img.onerror = () => {
          clearTimeout(timeout);
          resolve(defaultDimensions);
        };

        img.src = url;
      });
    }

    return defaultDimensions;
  } catch (error) {
    console.warn('Failed to get image dimensions:', error);
    return defaultDimensions;
  }
}

/**
 * Generate srcset attribute for responsive images
 * Creates multiple size variants for different viewport widths
 */
export function generateSrcSet(url: string, widths: number[] = [400, 800, 1200, 1600]): string {
  if (!url || url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('/')) {
    return '';
  }

  try {
    const srcsetParts = widths.map(width => {
      const optimizedUrl = optimizeImage(url, { width, format: 'webp' });
      return `${optimizedUrl} ${width}w`;
    });

    return srcsetParts.join(', ');
  } catch (error) {
    console.warn('Failed to generate srcset:', error);
    return '';
  }
}

/**
 * Advanced image optimization with full configuration support
 * Returns complete image attributes for optimal performance
 */
export function optimizeImageAdvanced(
  url: string,
  config: ImageOptimizationConfigAdvanced = {}
): OptimizedImageResult {
  const {
    width,
    height,
    quality = 80,
    format = 'webp',
    fit = 'cover',
    dimensions,
    priority = false,
    srcset = false,
    sizes,
  } = config;

  // Base optimized URL
  const src = optimizeImage(url, { width, height, quality, format, fit });

  const result: OptimizedImageResult = {
    src,
    loading: priority ? 'eager' : 'lazy',
  };

  // Add fetchpriority for priority images
  if (priority) {
    result.fetchpriority = 'high';
  }

  // Add explicit dimensions if provided
  if (dimensions) {
    result.width = dimensions.width;
    result.height = dimensions.height;
  } else if (width && height) {
    result.width = width;
    result.height = height;
  }

  // Generate responsive srcset if requested
  if (srcset && width && width > 400) {
    const widths = [
      Math.floor(width * 0.5),
      width,
      Math.floor(width * 1.5),
      Math.floor(width * 2),
    ].filter(w => w >= 400);
    
    result.srcset = generateSrcSet(url, widths);
    result.sizes = sizes || `(max-width: ${width}px) 100vw, ${width}px`;
  }

  return result;
}

